import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { LOGO_BUG_URL } from '../../constants/brand';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { key: 'home', to: '/' },
    { key: 'about', to: '#about' },
    { key: 'scorecard', to: '#scorecard' },
    { key: 'resources', to: '#resources' },
    { key: 'events', to: '#events' },
    { key: 'social', to: '/social' },
  ];

  const scrollToSection = (id) => {
    if (id === '/') {
      if (location.pathname !== '/') {
        window.location.href = '/';
        return;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setMenuOpen(false);
      return;
    }

    if (location.pathname !== '/') {
      window.location.href = `/${id}`;
      return;
    }

    const element = document.getElementById(id.replace('#', ''));
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <img src={LOGO_BUG_URL} alt="" className="h-10 w-10 object-contain flex-shrink-0" aria-hidden />
          <span className={`font-bold text-xl ${scrolled ? 'text-black' : 'text-slate-900'} md:text-2xl`}>Mujeres y Riquezas</span>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map(link => (
            link.to.startsWith('#') ? (
              <button
                key={link.key}
                onClick={() => scrollToSection(link.to)}
                className={`text-sm font-medium hover:text-pink-600 transition-colors ${scrolled ? 'text-gray-800' : 'text-gray-900'}`}
              >
                {t(`nav.${link.key}`)}
              </button>
            ) : (
              <Link
                key={link.key}
                to={link.to}
                className={`text-sm font-medium hover:text-pink-600 transition-colors ${scrolled ? 'text-gray-800' : 'text-gray-900'}`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            )
          ))}
          <button 
            onClick={toggleLanguage}
            className="flex items-center gap-1 px-3 py-1 rounded-full border border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white transition-all text-sm font-bold"
          >
            <SafeIcon icon={FiIcons.FiGlobe} className="text-lg" />
            {language.toUpperCase()}
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-pink-600"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <SafeIcon icon={menuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-3xl" />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-gray-100 py-4 flex flex-col items-center gap-4">
          {navLinks.map(link => (
            link.to.startsWith('#') ? (
              <button
                key={link.key}
                onClick={() => scrollToSection(link.to)}
                className="text-gray-800 text-lg font-medium"
              >
                {t(`nav.${link.key}`)}
              </button>
            ) : (
              <Link
                key={link.key}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="text-gray-800 text-lg font-medium"
              >
                {t(`nav.${link.key}`)}
              </Link>
            )
          ))}
          <button 
            onClick={() => { toggleLanguage(); setMenuOpen(false); }}
            className="mt-2 px-6 py-2 rounded-full bg-pink-600 text-white font-bold"
          >
            Switch to {language === 'es' ? 'English' : 'Español'}
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;